import React, { useState, useEffect, useMemo } from "react";
import axios from "../axios";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";
import { sl } from "date-fns/locale";
import { useWidth } from "../hooks/useWidth";
import { useAuth } from "../context/AuthContext";

/**
 * Utility function to format numeric values to one decimal place.
 */
function formatNumber(value) {
    if (typeof value === "number") {
        return value.toFixed(1);
    }
    if (!isNaN(parseFloat(value))) {
        return parseFloat(value).toFixed(1);
    }
    return value; // If not numeric, return as is
}

const AdminDashboardStatistics = () => {
    const { isLower } = useWidth(640);
    const { user } = useAuth();
    const [selectedYear, setSelectedYear] = useState(
        parseInt(format(new Date(), "yyyy"))
    );
    const [currentMonth, setCurrentMonth] = useState(
        format(new Date(), "LLLL", { locale: sl })
    );
    const [extendedMonthlyData, setExtendedMonthlyData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Check user role
    if (
        user?.role !== "admin" &&
        user?.role !== "owner" &&
        user?.role !== "manager"
    ) {
        return null;
    }

    const months = [
        "januar",
        "februar",
        "marec",
        "april",
        "maj",
        "junij",
        "julij",
        "avgust",
        "september",
        "oktober",
        "november",
        "december",
        "letno",
    ];

    // Fetch data effect
    useEffect(() => {
        async function fetchMonthlyData(year) {
            setLoading(true);
            try {
                const response = await axios.get(
                    `/api/admin/metrics/monthly/${year}`,
                    {
                        withCredentials: true,
                    }
                );

                const monthlyData = response.data;

                // Map the data to our frontend structure with detailed logging
                const mappedData = {};
                Object.entries(monthlyData).forEach(([key, value]) => {
                    if (key === "letno") {
                        mappedData["letno"] = value;
                    } else {
                        const monthKey = key.toLowerCase();
                        mappedData[monthKey] = value;
                    }
                });

                setExtendedMonthlyData(mappedData);
                setError(null);
            } catch (error) {
                setError(
                    "Napaka pri nalaganju podatkov. Prosim, poskusite znova kasneje."
                );
            } finally {
                setLoading(false);
            }
        }

        fetchMonthlyData(selectedYear);
    }, [selectedYear]);

    // Get data for current month
    const dataForCurrentMonth = extendedMonthlyData[currentMonth] || [];
    const hasData = dataForCurrentMonth.length > 0;

    // Debug logging for current data
    useEffect(() => {
        if (extendedMonthlyData && currentMonth) {
            const currentData = extendedMonthlyData[currentMonth];

            if (currentData && currentData.length > 0) {
                currentData.forEach((entry, index) => {});
            }
        }
    }, [extendedMonthlyData, currentMonth]);

    // Prepare chart data
    const enoteChartData = useMemo(() => {
        if (!hasData || !Array.isArray(dataForCurrentMonth)) {
            return [];
        }

        const chartData = dataForCurrentMonth
            .map((d) => {
                // Skip rows with no data
                if (!d || !d.name) {
                    console.error("Row missing name:", d);
                    return null;
                }

                // Ensure enote object exists
                const enote = d.enote || {};

                // Create chart data with explicit number conversion and null checks
                const processedData = {
                    name: d.name.trim(),
                    Preklicane:
                        typeof enote.preklicane === "number"
                            ? enote.preklicane
                            : 0,
                    Dobljene:
                        typeof enote.dobljene === "number" ? enote.dobljene : 0,
                    LK: typeof enote.lk === "number" ? enote.lk : 0,
                };

                return processedData;
            })
            .filter(Boolean);

        return chartData;
    }, [hasData, dataForCurrentMonth, currentMonth]);

    const strankeChartData = useMemo(() => {
        if (!hasData || !Array.isArray(dataForCurrentMonth)) return [];

        return dataForCurrentMonth
            .map((d) => {
                if (!d || !d.name) return null;

                const stranke = d.stranke || {};

                const chartData = {
                    name: d.name.trim(),
                    "Število vnešenih":
                        typeof stranke.stVnesen === "number"
                            ? stranke.stVnesen
                            : 0,
                    "Št. strank":
                        typeof stranke.stStrank === "number"
                            ? stranke.stStrank
                            : 0,
                    "Št. Ponudbe":
                        typeof stranke.stPonudbe === "number"
                            ? stranke.stPonudbe
                            : 0,
                    Križanje:
                        typeof stranke.krizanje === "number"
                            ? stranke.krizanje
                            : 0,
                    "Org. Stranke":
                        typeof stranke.orgStranke === "number"
                            ? stranke.orgStranke
                            : 0,
                    "Org. ogledi":
                        typeof stranke.orgOgledi === "number"
                            ? stranke.orgOgledi
                            : 0,
                    Silosi:
                        typeof stranke.silosi === "number" ? stranke.silosi : 0,
                    Projekti:
                        typeof stranke.projekti === "number"
                            ? stranke.projekti
                            : 0,
                    Preklicane:
                        typeof stranke.preklicane === "number"
                            ? stranke.preklicane
                            : 0,
                };

                return chartData;
            })
            .filter(Boolean);
    }, [hasData, dataForCurrentMonth]);

    const prevzemiChartData = useMemo(() => {
        if (!hasData || !Array.isArray(dataForCurrentMonth)) return [];

        return dataForCurrentMonth
            .map((d) => {
                if (!d || !d.name) return null;

                const prevzemi = d.prevzemi || {};

                const chartData = {
                    name: d.name.trim(),
                    "Prekl.":
                        typeof prevzemi.preklicane2 === "number"
                            ? prevzemi.preklicane2
                            : 0,
                    Realizirano:
                        typeof prevzemi.realizirano === "number"
                            ? prevzemi.realizirano
                            : 0,
                    "Faktor uspešnosti":
                        typeof prevzemi.faktorUspesnosti === "number"
                            ? prevzemi.faktorUspesnosti
                            : 0,
                };

                return chartData;
            })
            .filter(Boolean);
    }, [hasData, dataForCurrentMonth]);

    function getMonthLabel(m) {
        if (m === "letno") {
            return "Leto Skupaj";
        }
        // Capitalize first letter of month
        return m.charAt(0).toUpperCase() + m.slice(1);
    }

    const faktorData = useMemo(() => {
        if (!hasData || !Array.isArray(dataForCurrentMonth)) return [];

        // Find the maximum faktorUspesnosti value
        const maxFaktor = Math.max(
            ...dataForCurrentMonth
                .map((d) => d?.prevzemi?.faktorUspesnosti)
                .filter((val) => typeof val === "number")
        );

        // If no valid values found, set a fallback
        const maxValue = maxFaktor > 0 ? maxFaktor : 1;

        return dataForCurrentMonth
            .map((d) => {
                if (!d || !d.name) return null;

                const prevzemi = d.prevzemi || {};

                const chartData = {
                    name: d.name.trim(),
                    "Faktor uspešnosti":
                        typeof prevzemi.faktorUspesnosti === "number"
                            ? prevzemi.faktorUspesnosti
                            : 0,
                    "Faktor uspešnosti procenti":
                        typeof prevzemi.faktorUspesnosti === "number"
                            ? (prevzemi.faktorUspesnosti / maxValue) * 100
                            : 0,
                };

                return chartData;
            })
            .filter(Boolean)
            .sort(
                (a, b) =>
                    b["Faktor uspešnosti procenti"] -
                    a["Faktor uspešnosti procenti"]
            );
    }, [hasData, dataForCurrentMonth]);

    return (
        <div className="container px-4 py-8 mx-auto">
            {/* Header */}
            <div className="mb-8 text-center">
                <h1 className="mb-6 text-2xl font-bold">
                    Pregled Statistike{" "}
                    <span className="text-red-600">ADMINISTRATORJEV</span>
                </h1>
                <div className="flex justify-center gap-2">
                    <select
                        value={selectedYear}
                        onChange={(e) =>
                            setSelectedYear(Number(e.target.value))
                        }
                        className="px-4 py-2 bg-white border rounded-lg"
                    >
                        <option value={2024}>2024</option>
                        <option value={2025}>2025</option>
                        <option value={2026}>2026</option>
                    </select>
                    <select
                        value={currentMonth}
                        onChange={(e) => setCurrentMonth(e.target.value)}
                        className="px-4 py-2 bg-white border rounded-lg"
                    >
                        {months.map((month) => (
                            <option key={month} value={month}>
                                {getMonthLabel(month)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-b-2 border-gray-900 rounded-full animate-spin"></div>
                </div>
            )}

            {error && (
                <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded">
                    {error}
                </div>
            )}

            {hasData && (
                <div className="space-y-8">
                    {/* ENOTE Section */}
                    <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                        <div className="px-6 py-4 border-b border-blue-100 bg-blue-50">
                            <h2 className="text-xl font-bold text-blue-900">
                                ENOTE
                            </h2>
                        </div>
                        <div className="p-6">
                            {/* ENOTE Table */}
                            <div className="mb-6 overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                className="sticky left-0 px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase bg-gray-50"
                                            >
                                                Ime
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Nove
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Preklicane
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Dobljene
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                LK
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Skupaj
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                %
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                NJS
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Ne želi
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Delamo že
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Neaktivne
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {dataForCurrentMonth.map(
                                            (row, index) => (
                                                <tr
                                                    key={index}
                                                    className={
                                                        index % 2 === 0
                                                            ? "bg-white"
                                                            : "bg-gray-50"
                                                    }
                                                >
                                                    <td className="sticky left-0 px-3 py-2 text-sm font-medium whitespace-nowrap bg-inherit">
                                                        {row.name}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.enote.nove
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-red-600 whitespace-nowrap">
                                                        {formatNumber(
                                                            row.enote.preklicane
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-green-600 whitespace-nowrap">
                                                        {formatNumber(
                                                            row.enote.dobljene
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-blue-600 whitespace-nowrap">
                                                        {formatNumber(
                                                            row.enote.lk
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm font-medium whitespace-nowrap">
                                                        {formatNumber(
                                                            row.enote.skupaj
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {row.enote.percentage.toFixed(
                                                            2
                                                        )}
                                                        %
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.enote.njs
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.enote.neZeli
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.enote.delamoZa
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.enote.neaktivne
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* ENOTE Chart */}
                            <div className="h-[500px] sm:h-[600px] w-full mt-8 sm:mt-10 sm:mx-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={enoteChartData}
                                        margin={{
                                            top: 20,
                                            right: 0,
                                            left: 0,
                                            bottom: 100,
                                        }}
                                        barSize={30}
                                        maxBarSize={40}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            angle={-45}
                                            textAnchor="end"
                                            height={100}
                                            interval={0}
                                            tick={{ fontSize: 12 }}
                                            tickMargin={25}
                                            tickFormatter={(value) => {
                                                return isLower ? "" : value;
                                            }}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            width={isLower ? 5 : 45}
                                            tickMargin={5}
                                            domain={[0, "auto"]}
                                            tickFormatter={(value) => {
                                                return isLower ? "" : value;
                                            }}
                                        />
                                        <Tooltip
                                            formatter={(value) =>
                                                formatNumber(value)
                                            }
                                            contentStyle={{
                                                fontSize: "1rem",
                                                padding: "0.75rem",
                                                backgroundColor:
                                                    "rgba(255, 255, 255, 0.95)",
                                                border: "1px solid #e5e7eb",
                                            }}
                                            labelStyle={{
                                                fontWeight: "bold",
                                                marginBottom: "0.5rem",
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: "0.875rem",
                                                paddingTop: "1rem",
                                            }}
                                            verticalAlign="bottom"
                                            height={50}
                                            iconSize={12}
                                            iconType="circle"
                                        />
                                        <Bar
                                            dataKey="Preklicane"
                                            fill="#EF4444"
                                            name="Preklicane"
                                            radius={[4, 4, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="Dobljene"
                                            fill="#10B981"
                                            name="Dobljene"
                                            radius={[4, 4, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="LK"
                                            fill="#3B82F6"
                                            name="LK"
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* STRANKE Section */}
                    <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                        <div className="px-6 py-4 border-b border-green-100 bg-green-50">
                            <h2 className="text-xl font-bold text-green-900">
                                STRANKE
                            </h2>
                        </div>
                        <div className="p-6">
                            {/* STRANKE Table */}
                            <div className="mb-6 overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                className="sticky left-0 px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase bg-gray-50"
                                            >
                                                Ime
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Število vnešenih
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Št. strank
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Št. Ponudbe
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Križanje
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Org. Stranke
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Org. ogledi
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Silosi
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Projekti
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Preklicane
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {dataForCurrentMonth.map(
                                            (row, index) => (
                                                <tr
                                                    key={index}
                                                    className={
                                                        index % 2 === 0
                                                            ? "bg-white"
                                                            : "bg-gray-50"
                                                    }
                                                >
                                                    <td className="sticky left-0 px-3 py-2 text-sm font-medium whitespace-nowrap bg-inherit">
                                                        {row.name}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.stranke.stVnesen
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.stranke.stStrank
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.stranke
                                                                .stPonudbe
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.stranke.krizanje
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.stranke
                                                                .orgStranke
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.stranke
                                                                .orgOgledi
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.stranke.silosi
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatNumber(
                                                            row.stranke.projekti
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-red-600 whitespace-nowrap">
                                                        {formatNumber(
                                                            row.stranke
                                                                .preklicane
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* STRANKE Chart */}
                            <div className="h-[500px] sm:h-[600px] w-full mt-8 sm:mt-10 sm:mx-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={strankeChartData}
                                        margin={{
                                            top: 20,
                                            right: 0,
                                            left: 0,
                                            bottom: 100,
                                        }}
                                        barSize={30}
                                        maxBarSize={40}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            angle={-45}
                                            textAnchor="end"
                                            height={100}
                                            interval={0}
                                            tick={{ fontSize: 12 }}
                                            tickMargin={25}
                                            tickFormatter={(value) => {
                                                return isLower ? "" : value;
                                            }}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            width={isLower ? 5 : 45}
                                            tickMargin={5}
                                            domain={[0, "auto"]}
                                            tickFormatter={(value) => {
                                                return isLower ? "" : value;
                                            }}
                                        />
                                        <Tooltip
                                            formatter={(value) =>
                                                formatNumber(value)
                                            }
                                            contentStyle={{
                                                fontSize: "1rem",
                                                padding: "0.75rem",
                                                backgroundColor:
                                                    "rgba(255, 255, 255, 0.95)",
                                                border: "1px solid #e5e7eb",
                                            }}
                                            labelStyle={{
                                                fontWeight: "bold",
                                                marginBottom: "0.5rem",
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: "0.875rem",
                                                paddingTop: "1rem",
                                            }}
                                            verticalAlign="bottom"
                                            height={50}
                                            iconSize={12}
                                            iconType="circle"
                                        />
                                        <Bar
                                            dataKey="Število vnešenih"
                                            fill="#3B82F6"
                                            radius={[4, 4, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="Št. strank"
                                            fill="#10B981"
                                            radius={[4, 4, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="Št. Ponudbe"
                                            fill="#F59E0B"
                                            radius={[4, 4, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="Križanje"
                                            fill="#6366F1"
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* PREVZEMI Section */}
                    <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                        <div className="px-4 py-3 border-b border-purple-100 bg-purple-50 sm:px-6 sm:py-4">
                            <h2 className="text-lg font-bold text-purple-900 sm:text-xl">
                                PREVZEMI
                            </h2>
                        </div>
                        <div className="p-2 sm:p-6">
                            {/* PREVZEMI Table */}
                            <div className="mb-6 -mx-2 overflow-x-auto sm:mx-0">
                                <div className="inline-block min-w-full align-middle">
                                    <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th
                                                        scope="col"
                                                        className="sticky left-0 px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-3 bg-gray-50"
                                                    >
                                                        Ime
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-3"
                                                    >
                                                        Prekl.
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-3"
                                                    >
                                                        Real.
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {dataForCurrentMonth.map(
                                                    (row, index) => (
                                                        <tr
                                                            key={index}
                                                            className={
                                                                index % 2 === 0
                                                                    ? "bg-white"
                                                                    : "bg-gray-50"
                                                            }
                                                        >
                                                            <td className="sticky left-0 px-2 py-2 text-xs font-medium sm:px-3 whitespace-nowrap sm:text-sm bg-inherit">
                                                                {row.name}
                                                            </td>
                                                            <td className="px-2 py-2 text-xs text-red-600 sm:px-3 whitespace-nowrap sm:text-sm">
                                                                {formatNumber(
                                                                    row.prevzemi
                                                                        .preklicane2
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-2 text-xs text-green-600 sm:px-3 whitespace-nowrap sm:text-sm">
                                                                {formatNumber(
                                                                    row.prevzemi
                                                                        .realizirano
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            {/* PREVZEMI Chart */}
                            <div className="h-[500px] sm:h-[600px] w-full mt-8 sm:mt-10 sm:mx-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={prevzemiChartData}
                                        margin={{
                                            top: 20,
                                            right: 0,
                                            left: 0,
                                            bottom: 100,
                                        }}
                                        barSize={30}
                                        maxBarSize={40}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            angle={-45}
                                            textAnchor="end"
                                            height={100}
                                            interval={0}
                                            tick={{ fontSize: 12 }}
                                            tickMargin={25}
                                            tickFormatter={(value) => {
                                                return isLower ? "" : value;
                                            }}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            width={isLower ? 5 : 45}
                                            tickMargin={5}
                                            domain={[0, "auto"]}
                                            tickFormatter={(value) => {
                                                return isLower ? "" : value;
                                            }}
                                        />
                                        <Tooltip
                                            formatter={(value) =>
                                                formatNumber(value)
                                            }
                                            contentStyle={{
                                                fontSize: "1rem",
                                                padding: "0.75rem",
                                                backgroundColor:
                                                    "rgba(255, 255, 255, 0.95)",
                                                border: "1px solid #e5e7eb",
                                            }}
                                            labelStyle={{
                                                fontWeight: "bold",
                                                marginBottom: "0.5rem",
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: "0.875rem",
                                                paddingTop: "1rem",
                                            }}
                                            verticalAlign="bottom"
                                            height={50}
                                            iconSize={12}
                                            iconType="circle"
                                        />
                                        <Bar
                                            dataKey="Prekl."
                                            fill="#F87171"
                                            radius={[4, 4, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="Realizirano"
                                            fill="#10B981"
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* FAKTOR USPEŠNOSTI Section */}
                    <div className="p-6 bg-white rounded-lg shadow-sm">
                        <div className="mb-6 overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="sticky left-0 px-4 py-3 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase bg-gray-50"
                                        >
                                            Ime
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase"
                                        >
                                            Faktor uspešnosti
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {faktorData.map((item, index) => (
                                        <tr
                                            key={item.name}
                                            className={
                                                index % 2 === 0
                                                    ? "bg-white"
                                                    : "bg-gray-50"
                                            }
                                        >
                                            <td className="sticky left-0 px-4 py-3 text-sm font-medium whitespace-nowrap bg-inherit">
                                                {item.name}
                                            </td>
                                            <td
                                                className="px-4 py-3 text-sm font-bold whitespace-nowrap"
                                                style={{ color: "#d31245" }}
                                            >
                                                {`${item[
                                                    "Faktor uspešnosti"
                                                ].toFixed(2)}`}
                                            </td>
                                            <td
                                                className="px-4 py-3 text-sm font-bold whitespace-nowrap"
                                                style={{ color: "#d31245" }}
                                            >
                                                {`${item[
                                                    "Faktor uspešnosti procenti"
                                                ].toFixed(1)}%`}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="h-[300px] sm:h-[400px] mt-6">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={faktorData}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: -15,
                                        bottom: 60,
                                    }}
                                    barSize={40}
                                    maxBarSize={50}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                        interval={0}
                                        tick={{ fontSize: 12 }}
                                        tickMargin={25}
                                        tickFormatter={(value) => {
                                            return isLower ? "" : value;
                                        }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12 }}
                                        width={isLower ? 5 : 45}
                                        tickMargin={5}
                                        domain={[0, "auto"]}
                                        tickFormatter={(value) => {
                                            return isLower ? "" : value;
                                        }}
                                    />
                                    <Tooltip
                                        formatter={(value) =>
                                            `${value.toFixed(2)}`
                                        }
                                        contentStyle={{
                                            fontSize: "1rem",
                                            padding: "0.75rem",
                                            backgroundColor:
                                                "rgba(255, 255, 255, 0.95)",
                                            border: "1px solid #e5e7eb",
                                        }}
                                        labelStyle={{
                                            fontWeight: "bold",
                                            marginBottom: "0.5rem",
                                        }}
                                    />
                                    <Legend
                                        wrapperStyle={{
                                            fontSize: "0.875rem",
                                            paddingTop: "1rem",
                                        }}
                                        verticalAlign="bottom"
                                        height={40}
                                        iconSize={12}
                                        iconType="circle"
                                    />
                                    <Bar
                                        dataKey="Faktor uspešnosti"
                                        fill="#d31245"
                                        radius={[4, 4, 0, 0]}
                                        name="Faktor uspešnosti"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboardStatistics;
